import { ChangeEvent } from "react";
import { firestore } from "@foresting-tomorrow/api_types_firestore";

// material ui
import { Grid, Typography, Divider } from "@mui/material";

// project import
import Selector from "components/select/selector";
import { useTranslation } from "utils/locales/utilityFunctions";
import RawWoodInputInformationOrigin from "./rawWoodInputInformationOrigin";
import RawWoodInputInformationCertification from "./rawWoodInputInformationCertification";
import { InventoryCertificationFieldConfig } from "./rawWoodInputInformationCertification";
import RawWoodInputInformationSpeciesAssortment from "./rawWoodInputInformationSpeciesAssortment";
import RawWoodInputInformationChipStatus from "./rawWoodInputInformationChipStatus";
import { FirestoreInventoryItemProps } from "types/auth";
import { ActivityOption } from "components/estatesAndMapSelecter/getActivityOptions";
import { ContactType } from "components/shareData/shareDialog";
import {
  RawWoodInputInformationSpeciesHandle,
  RawWoodSpeciesItem,
} from "./rawWoodInputInformationSpecies";
import { RawWoodOptionProps } from "./options";

interface RawWoodInputInformationProps {
  variant: FirestoreInventoryItemProps["inventoryType"];
  showOriginSection: boolean;
  disableInputs: boolean;
  buyerApprovedDisable: boolean;
  countryCode: string;
  certification: string[];
  certificationNumbers: NonNullable<
    FirestoreInventoryItemProps["certificationNumbers"]
  >;
  certificationClaims: NonNullable<FirestoreInventoryItemProps["certificationClaims"]>;
  legacyCertificationNumber: string;
  originsOptions: ActivityOption[];
  origins?: ActivityOption[];
  responsibleOptions: ContactType[];
  responsible?: ContactType[];
  date: Date | null;
  initSpeciesArray?: RawWoodSpeciesItem[];
  speciesRef: React.RefObject<RawWoodInputInformationSpeciesHandle | null>;
  speciesOptions: any[];
  assortmentOptions: RawWoodOptionProps[];
  assortment: string;
  averageLogLength: string;
  averageLogDiameter: string;
  woodLogAssortmentOptions: RawWoodOptionProps[];
  woodLogAssortment: string;
  isChipped?: boolean;
  setCertification: (certification: string) => void;
  setCertificationNumbers: (
    certificationNumbers: NonNullable<
      FirestoreInventoryItemProps["certificationNumbers"]
    >
  ) => void;
  setCertificationClaims: (
    certificationClaims: NonNullable<FirestoreInventoryItemProps["certificationClaims"]>,
  ) => void;
  setOrigins: (v: any) => void;
  setResponsible: (v: any) => void;
  setDate: (date: Date | null) => void;
  setAssortment: (value: string) => void;
  setAverageLogLength: (event: ChangeEvent<HTMLInputElement>) => void;
  setAverageLogDiameter: (event: ChangeEvent<HTMLInputElement>) => void;
  setWoodLogAssortment: (value: string) => void;
  setIsChipped?: (value: boolean) => void;
}

const getCertificationFields = (
  variant: FirestoreInventoryItemProps["inventoryType"],
  countryCode: string,
  assortment: string,
): InventoryCertificationFieldConfig[] => {
  const commonFields: InventoryCertificationFieldConfig[] = [
    {
      tag: "fsc",
      claimOptions: firestore.inventoryFscClaimValues,
      showCertificateNumberInput: true,
    },
    {
      tag: "pefc",
      claimOptions: firestore.inventoryPefcClaimValues,
      showCertificateNumberInput: true,
    },
  ];

  if (variant === "WoodChipStack") {
    return [
      ...commonFields,
      { tag: "sbp", showCertificateNumberInput: true },
      { tag: "rbp", showCertificateNumberInput: false },
      ...(countryCode === "GB" ? [{ tag: "ukwas", showCertificateNumberInput: true } as const] : []),
    ];
  }

  if (variant === "WoodStack") {
    return [
      ...commonFields,
      ...(assortment === "energyWood"
        ? [{ tag: "sbp", showCertificateNumberInput: true } as const]
        : []),
      ...(countryCode === "GB" ? [{ tag: "ukwas", showCertificateNumberInput: true } as const] : []),
    ];
  }

  return [
    ...commonFields,
    ...(countryCode === "GB" ? [{ tag: "ukwas", showCertificateNumberInput: true } as const] : []),
  ];
};

const RawWoodInputInformation = ({
  variant,
  showOriginSection,
  disableInputs,
  buyerApprovedDisable,
  countryCode,
  certification,
  certificationNumbers,
  certificationClaims,
  legacyCertificationNumber,
  originsOptions,
  origins,
  responsibleOptions,
  responsible,
  date,
  initSpeciesArray,
  speciesRef,
  speciesOptions,
  assortmentOptions,
  assortment,
  averageLogLength,
  averageLogDiameter,
  woodLogAssortmentOptions,
  woodLogAssortment,
  isChipped = false,
  setCertification,
  setCertificationNumbers,
  setCertificationClaims,
  setOrigins,
  setResponsible,
  setDate,
  setAssortment,
  setAverageLogLength,
  setAverageLogDiameter,
  setWoodLogAssortment,
  setIsChipped,
}: RawWoodInputInformationProps) => {
  // --- Hooks --- //
  const { translate } = useTranslation();
  const certificationFields = getCertificationFields(variant, countryCode, assortment);

  // --- Render --- //
  if (variant === "WoodStack") {
    return (
      <>
        {showOriginSection && (
          <>
            <RawWoodInputInformationOrigin
              variant={variant}
              disableInputs={disableInputs || buyerApprovedDisable}
              originsOptions={originsOptions}
              origins={origins}
              responsibleOptions={responsibleOptions}
              responsible={responsible}
              date={date}
              handleChangeOrigins={setOrigins}
              handleChangeResponsible={setResponsible}
              handleChangeDate={setDate}
            />
            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
            </Grid>
          </>
        )}

        <RawWoodInputInformationSpeciesAssortment
          variant={variant}
          disableInputs={disableInputs || buyerApprovedDisable}
          initSpeciesArray={initSpeciesArray}
          speciesRef={speciesRef}
          speciesOptions={speciesOptions}
          assortmentOptions={assortmentOptions}
          assortment={assortment}
          averageLogLength={averageLogLength}
          averageLogDiameter={averageLogDiameter}
          setAssortment={setAssortment}
          setAverageLogLength={setAverageLogLength}
          setAverageLogDiameter={setAverageLogDiameter}
        />

        <Grid item xs={12}>
          <Divider sx={{ mt: 1 }} />
        </Grid>

        <RawWoodInputInformationCertification
          disableInputs={disableInputs || buyerApprovedDisable}
          certificationFields={certificationFields}
          certificationNumbers={certificationNumbers}
          certificationClaims={certificationClaims}
          legacyCertificationNumber={legacyCertificationNumber}
          fsc={certification.includes("fsc")}
          pefc={certification.includes("pefc")}
          ukwas={certification.includes("ukwas")}
          rbp={certification.includes("rbp")}
          sbp={certification.includes("sbp")}
          setCertification={setCertification}
          setCertificationNumbers={setCertificationNumbers}
          setCertificationClaims={setCertificationClaims}
        />
      </>
    );
  } else if (variant === "WoodChipStack") {
    return (
      <>
        {showOriginSection && (
          <>
            <RawWoodInputInformationOrigin
              variant={variant}
              disableInputs={disableInputs || buyerApprovedDisable}
              originsOptions={originsOptions}
              origins={origins}
              responsibleOptions={responsibleOptions}
              responsible={responsible}
              date={date}
              handleChangeOrigins={setOrigins}
              handleChangeResponsible={setResponsible}
              handleChangeDate={setDate}
            />
            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
            </Grid>
          </>
        )}

        <RawWoodInputInformationSpeciesAssortment
          variant={variant}
          disableInputs={disableInputs || buyerApprovedDisable}
          initSpeciesArray={initSpeciesArray}
          speciesRef={speciesRef}
          speciesOptions={speciesOptions}
          assortmentOptions={assortmentOptions}
          assortment={assortment}
          averageLogLength={averageLogLength}
          averageLogDiameter={averageLogDiameter}
          setAssortment={setAssortment}
          setAverageLogLength={setAverageLogLength}
          setAverageLogDiameter={setAverageLogDiameter}
        />

        {setIsChipped && (
          <RawWoodInputInformationChipStatus
            isChipped={isChipped}
            averageLogLength={averageLogLength}
            disabled={disableInputs || buyerApprovedDisable}
            setIsChipped={setIsChipped}
            setAverageLogLength={setAverageLogLength}
          />
        )}

        <Grid item xs={12}>
          <Divider sx={{ mt: 1 }} />
        </Grid>

        <RawWoodInputInformationCertification
          disableInputs={disableInputs || buyerApprovedDisable}
          certificationNumbers={certificationNumbers}
          certificationClaims={certificationClaims}
          legacyCertificationNumber={legacyCertificationNumber}
          certificationFields={certificationFields}
          fsc={certification.includes("fsc")}
          pefc={certification.includes("pefc")}
          ukwas={certification.includes("ukwas")}
          rbp={certification.includes("rbp")}
          sbp={certification.includes("sbp")}
          setCertification={setCertification}
          setCertificationNumbers={setCertificationNumbers}
          setCertificationClaims={setCertificationClaims}
        />
      </>
    );
  } else {
    return (
      <>
        {showOriginSection && (
          <>
            <RawWoodInputInformationOrigin
              variant={variant}
              disableInputs={disableInputs || buyerApprovedDisable}
              originsOptions={originsOptions}
              origins={origins}
              responsibleOptions={responsibleOptions}
              responsible={responsible}
              date={date}
              handleChangeOrigins={setOrigins}
              handleChangeResponsible={setResponsible}
              handleChangeDate={setDate}
            />
            <Grid item xs={12}>
              <Divider sx={{ mt: 1 }} />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Typography variant="h5">
            {translate("input-dialog-raw-wood-tab-information-assortment")}
          </Typography>
        </Grid>

        <Grid item xs={6} sm={12 / 3}>
          <Selector
            title={translate(
              "input-dialog-raw-wood-tab-information-assortment-type"
            )}
            value={woodLogAssortment}
            options={woodLogAssortmentOptions}
            label={true}
            fullWidth={true}
            error={false}
            disabled={disableInputs || buyerApprovedDisable}
            changeHandler={(e) => setWoodLogAssortment(e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ mt: 1 }} />
        </Grid>

        <RawWoodInputInformationCertification
          disableInputs={disableInputs || buyerApprovedDisable}
          certificationNumbers={certificationNumbers}
          certificationClaims={certificationClaims}
          legacyCertificationNumber={legacyCertificationNumber}
          certificationFields={certificationFields}
          fsc={certification.includes("fsc")}
          pefc={certification.includes("pefc")}
          ukwas={certification.includes("ukwas")}
          sbp={certification.includes("sbp")}
          rbp={certification.includes("rbp")}
          setCertification={setCertification}
          setCertificationNumbers={setCertificationNumbers}
          setCertificationClaims={setCertificationClaims}
        />
      </>
    );
  }
};

export default RawWoodInputInformation;
